import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import EntitySearchPicker from './EntitySearchPicker';

/**
 * One configurable comparison series.
 *
 * Layout:
 *   [color swatch] [type select] [searchable picker] [date override toggle] [x remove]
 *
 * When the user expands the date override, a small inline panel offers the
 * baseline-vs-shift presets that make year-over-year compares trivial:
 *   - Use global range
 *   - Last year (shift -1y from global)
 *   - Two years ago (shift -2y)
 *   - Custom (raw from/to inputs)
 */

const ENTITY_TYPES = ['Tenant', 'Category', 'Subcategory', 'Property', 'Floor', 'Group', 'Zone', 'Vertical'];

function shiftYears(range, years) {
    if (!range?.from || !range?.to) return null;
    const f = new Date(range.from);
    const t = new Date(range.to);
    f.setFullYear(f.getFullYear() - years);
    t.setFullYear(t.getFullYear() - years);
    return { from: f.toISOString().slice(0, 10), to: t.toISOString().slice(0, 10) };
}

export default function SeriesRow({ index, entry, color, globalRange, onChange, onRemove, removable }) {
    const { t } = useTranslation();
    const [showOverride, setShowOverride] = useState(!!entry.dateRange);

    const update = (patch) => onChange({ ...entry, ...patch });

    const setPreset = (key) => {
        if (key === 'global') update({ dateRange: null });
        else if (key === 'last-year') update({ dateRange: shiftYears(globalRange, 1) });
        else if (key === 'two-years') update({ dateRange: shiftYears(globalRange, 2) });
    };

    const effective = entry.dateRange || globalRange;

    return (
        <div style={card}>
            <div style={topRow}>
                <span style={{ ...swatch, background: color }} title={t('Series color')} />
                <select
                    value={entry.type}
                    onChange={(e) => update({ type: e.target.value, id: '', label: '' })}
                    style={typeSel}
                >
                    {ENTITY_TYPES.map(et => <option key={et} value={et}>{t(et)}</option>)}
                </select>
                <EntitySearchPicker
                    type={entry.type}
                    value={{ id: entry.id, label: entry.label }}
                    onChange={(next) => update({ id: next.id, label: next.label })}
                />
                <button
                    type="button"
                    onClick={() => setShowOverride(v => !v)}
                    className={`v3-btn ${entry.dateRange ? 'v3-btn-primary' : ''}`}
                    title={t('Override date range for this series only')}
                    style={{ fontSize: 12 }}
                >
                    {entry.dateRange ? `↳ ${entry.dateRange.from} → ${entry.dateRange.to}` : t('Date override')}
                </button>
                {removable && (
                    <button
                        type="button"
                        onClick={onRemove}
                        className="v3-btn"
                        title={t('Remove series')}
                        style={{ fontSize: 12, padding: '6px 10px' }}
                    >
                        ✕
                    </button>
                )}
            </div>

            {showOverride && (
                <div style={panel}>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                        <button type="button" className={`v3-btn ${!entry.dateRange ? 'v3-btn-primary' : ''}`} onClick={() => setPreset('global')} style={btnSm}>{t('Use global')}</button>
                        <button type="button" className="v3-btn" onClick={() => setPreset('last-year')} style={btnSm}>{t('Same range, last year')}</button>
                        <button type="button" className="v3-btn" onClick={() => setPreset('two-years')} style={btnSm}>{t('2 years ago')}</button>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <input
                            type="date"
                            value={effective?.from || ''}
                            onChange={(e) => update({ dateRange: { from: e.target.value, to: effective?.to } })}
                            style={dateInput}
                        />
                        <span style={{ color: 'var(--v3-text-muted)', fontSize: 12 }}>→</span>
                        <input
                            type="date"
                            value={effective?.to || ''}
                            onChange={(e) => update({ dateRange: { from: effective?.from, to: e.target.value } })}
                            style={dateInput}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}

const card = {
    padding: 12,
    borderRadius: 10,
    border: '1px solid var(--v3-glass-border)',
    background: 'var(--v3-bg-surface)',
    marginBottom: 10,
};
const topRow = { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' };
const panel = {
    marginTop: 10,
    paddingTop: 10,
    borderTop: '1px dashed var(--v3-glass-border)',
};
const swatch = { width: 14, height: 14, borderRadius: 4, flexShrink: 0 };
const typeSel = {
    padding: '8px 12px',
    background: 'var(--v3-bg-elevated, var(--v3-bg-surface))',
    border: '1px solid var(--v3-glass-border)',
    borderRadius: 8,
    color: 'var(--v3-text-primary)',
    fontSize: 13,
    outline: 'none',
    width: 130,
};
const dateInput = {
    padding: '6px 10px',
    background: 'var(--v3-bg-surface)',
    border: '1px solid var(--v3-glass-border)',
    borderRadius: 8,
    color: 'var(--v3-text-primary)',
    fontSize: 12,
};
const btnSm = { fontSize: 11, padding: '4px 10px' };
